import type { DealOffer } from "@/components/marketplace/deal-offers-types";
import { sortOffersByItemPrice } from "@/components/marketplace/sortOffersByItemPrice";
import type { FormatCategoryWithOther } from "@garimpa/lib/formatGroups";

type ReleaseOffer = Pick<
  DealOffer,
  | "offerId"
  | "matchedReleaseIds"
  | "currentPrice"
  | "currentCurrency"
  | "formatCategory"
  | "mediaCategories"
>;

/** Separate exact-edition sellers from alternatives, keeping one cheaper edition per format. */
export function selectReleaseOffers<T extends ReleaseOffer>(
  offers: T[],
  releaseId: string,
  format?: FormatCategoryWithOther,
) {
  const matchingOffers = format
    ? offers.filter(
        (offer) => offer.formatCategory === format || offer.mediaCategories?.includes(format),
      )
    : offers;
  const sorted = sortOffersByItemPrice(matchingOffers);
  const releaseOffers = sorted.filter((offer) => offer.matchedReleaseIds.includes(releaseId));
  const otherOffers = sorted.filter((offer) => !offer.matchedReleaseIds.includes(releaseId));
  const bestOffer = releaseOffers[0];
  const alternatives = otherOffers.filter((offer, index, others) => {
    const price = offer.currentPrice === null ? NaN : Number(offer.currentPrice);
    if (!Number.isFinite(price) || !offer.currentCurrency) return false;
    if (
      bestOffer &&
      (offer.currentCurrency !== bestOffer.currentCurrency ||
        price >= Number(bestOffer.currentPrice))
    )
      return false;
    return (
      others.findIndex((candidate) => candidate.formatCategory === offer.formatCategory) === index
    );
  });
  return { releaseOffers, otherOffers, alternatives, cheapestOfferId: sorted[0]?.offerId ?? null };
}
